import { Box, Paper } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { useShallow } from "zustand/shallow"

import { CountChip, GoodsReceiptProductLayout } from "../../../../components"
import { calculatePurchaseOrderTotalsFromPalletRows } from "../../../../utils"

import type { GoodsReceiptPalletRow, GoodsReceiptProductDetail, OrderLine } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatNumber } from "@/utils/number"

interface StoredPurchaseOrderLineProps {
  purchaseOrderLine: OrderLine
  goodsReceiptLines: GoodsReceiptProductDetail[]
  goodsReceiptPalletRows: GoodsReceiptPalletRow[]
}

export const StoredPurchaseOrderLine: FC<StoredPurchaseOrderLineProps> = ({
  purchaseOrderLine,
  goodsReceiptLines,
  goodsReceiptPalletRows,
}) => {
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const product = purchaseOrderLine.product!
  const uom = uoms.find((uom) => uom.id === purchaseOrderLine.uomId)

  const totals = useMemo(() => {
    return calculatePurchaseOrderTotalsFromPalletRows([purchaseOrderLine], goodsReceiptLines, goodsReceiptPalletRows)
  }, [purchaseOrderLine, goodsReceiptLines, goodsReceiptPalletRows])

  return (
    <Paper elevation={0} className='rounded-md' data-testid='purchase-order-line'>
      <GoodsReceiptProductLayout product={product}>
        <Box className='flex flex-row flex-wrap items-center justify-end gap-2'>
          <CountChip label='Pallets' value={String(totals.palletsCount)} data-testid='pallets-count' />
          <CountChip label='Units' value={String(totals.unitsCount)} data-testid='units-count' />
          <CountChip
            label='Total UoM'
            value={`${formatNumber(totals.quantity ?? 0)} ${uom?.name ?? ""}`}
            data-testid='total-uom-count'
          />
        </Box>
      </GoodsReceiptProductLayout>
    </Paper>
  )
}
